/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * Org User Management API Service
 * CRUD endpoints for /api/v1/venta/admin/users
 */

import { get, post, put } from '@/services/api/client';
import type { ApiResponse } from '@/services/api/types';
import type { PagedResult } from '../types/product';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface LocationSummaryDto {
  id: string;
  name: string;
  code: string;
  active: boolean;
}

/**
 * Per-user override for the current-day financial restriction.
 * DEFAULT follows the organisation's role list.
 */
export type CurrentDayRestriction = 'DEFAULT' | 'ALWAYS' | 'NEVER';

export interface OrgUser {
  id: string;
  // Identity
  email: string;
  firstName: string;
  lastName: string;
  displayName: string;
  avatarUrl: string | null;
  phoneNumber: string | null;
  language: string;
  // Role & Location
  role: string;
  roleId: string | null;
  roleName: string | null;
  locations: LocationSummaryDto[];
  allLocationAccess: boolean;
  // Current-day financial restriction (per-user override)
  currentDayRestriction: CurrentDayRestriction;
  // Account Status
  status: string;
  createdAt: string;
  // Multi-Factor Authentication
  mfaEnabled: boolean;
  mfaEnforced: boolean;
  mfaSetupCompletedAt: string | null;
  // Password Policy
  mustChangePassword: boolean;
  passwordChangedAt: string | null;
  passwordExpiresAt: string | null;
  // Account Security
  emailVerified: boolean;
  failedLoginAttempts: number;
  lockedAt: string | null;
  lockedUntil: string | null;
  lockoutCount: number;
  // Session / Activity
  lastLoginAt: string | null;
  lastLoginIp: string | null;
  lastLoginDevice: string | null;
  lastActivityAt: string | null;
}

export interface OrgUserStats {
  totalUsers: number;
  activeUsers: number;
  inactiveUsers: number;
  roleCount: number;
}

export interface CreateOrgUserRequest {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  roleId: string;
  locationIds?: string[];
  language?: string;
  password: string;
  sendWelcomeEmail?: boolean;
  requireMfa?: boolean;
}

export interface UpdateOrgUserRequest {
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  roleId: string;
  locationIds?: string[];
  status?: string;
  language?: string;
  mfaEnforced?: boolean;
  mustChangePassword?: boolean;
  currentDayRestriction?: CurrentDayRestriction;
}

export interface OrgUserListParams {
  search?: string;
  roleId?: string;
  status?: string;
  page?: number;
  size?: number;
}

// ─── Service Functions ──────────────────────────────────────────────────────

const BASE = '/venta/admin/users';

export async function listUsers(params: OrgUserListParams = {}): Promise<PagedResult<OrgUser>> {
  const queryParams: Record<string, unknown> = {};
  if (params.search) queryParams.search = params.search;
  if (params.roleId) queryParams.roleId = params.roleId;
  if (params.status) queryParams.status = params.status;
  if (params.page !== undefined) queryParams.page = params.page;
  if (params.size !== undefined) queryParams.size = params.size;

  const response = await get<ApiResponse<PagedResult<OrgUser>>>(BASE, queryParams);
  return response.data;
}

export async function getStats(): Promise<OrgUserStats> {
  const response = await get<ApiResponse<OrgUserStats>>(`${BASE}/stats`);
  return response.data;
}

export async function getUser(id: string): Promise<OrgUser> {
  const response = await get<ApiResponse<OrgUser>>(`${BASE}/${id}`);
  return response.data;
}

export async function createUser(data: CreateOrgUserRequest): Promise<OrgUser> {
  const response = await post<ApiResponse<OrgUser>>(BASE, data);
  return response.data;
}

export async function updateUser(id: string, data: UpdateOrgUserRequest): Promise<OrgUser> {
  const response = await put<ApiResponse<OrgUser>>(`${BASE}/${id}`, data);
  return response.data;
}

export async function updateStatus(id: string, status: string): Promise<OrgUser> {
  const response = await put<ApiResponse<OrgUser>>(`${BASE}/${id}/status`, undefined, {
    params: { status },
  });
  return response.data;
}

export async function forcePasswordReset(id: string): Promise<void> {
  await post<ApiResponse<void>>(`${BASE}/${id}/force-password-reset`);
}

export async function resendWelcomeEmail(id: string): Promise<void> {
  await post<ApiResponse<void>>(`${BASE}/${id}/welcome-email`);
}

export async function getMyLocations(): Promise<LocationSummaryDto[]> {
  const response = await get<ApiResponse<LocationSummaryDto[]>>('/venta/users/me/locations');
  return response.data;
}

export const orgUsersService = {
  listUsers,
  getStats,
  getUser,
  createUser,
  updateUser,
  updateStatus,
  forcePasswordReset,
  resendWelcomeEmail,
  getMyLocations,
};
